import React, { Component } from 'react';
import Pusher from 'pusher-js';
import LiveStreamOverlay from '../live_stream_overlay/live_stream_overlay';

const DEFAULT_SECONDS_PER_SLIDE = 10;
const MIN_SECONDS_PER_SLIDE = 1;
const MAX_SECONDS_PER_SLIDE = 90;

// Per-slide crossfade timing (#322). tvOS-parity: 0.5s fade to black
// followed by a 0.5s fade in of the next slide. During the fade window
// the player's advance timer is paused; the swap happens at the trough
// (opacity 0) so the incoming asset never bleeds through the outgoing.
const FADE_MS = 500;

// Preload / retention budget (S3 spend work).
//
// PRELOAD_AHEAD = 1 — after the current slide settles, warm exactly the
// next one. Prefetching the whole playlist would trade "blank frame on
// advance" for "burst of downloads for slides the device may never
// reach", which is the opposite of the goal. One ahead is enough: the
// slide interval (>= 1s, typically 10s) dwarfs the fetch.
//
// RETAINED_IMAGES = 10 — decoded images are kept in an LRU Map so a
// loop's second pass doesn't depend solely on the browser HTTP cache.
// Ten 1080p decoded frames is on the order of 80 MB of bitmap in the
// worst case, which is within budget for the browsers this player runs
// on and covers a typical playlist's working set.
const PRELOAD_AHEAD = 1;
const RETAINED_IMAGES = 10;

// Resolve the full-resolution URL for a playlist entry.
//
// Deliberately NOT the `thumbnail_url` / `still_url` derivatives — this
// is the actual viewing surface. Derivatives are ~480px previews for
// admin grids only (docs/billboard_media_caching_contract.md §1.4).
function slideUrl(item) {
    if (!item) return null;
    return item.billboard_media_item ? item.billboard_media_item.url : item.url;
}

// Per-slide image fit (#271, #319). Mirrors `objectFitClassFor` in the
// SPA (frontend/src/pages/billboard/PlaylistDetail.tsx) — see
// docs/billboard_image_display_modes.md for the client contract. The
// TV-display route was hardcoded to `contain`, so any per-slide choice
// besides the default silently rendered as scale_to_fit on TVs.
function objectFitFor(mode) {
    switch (mode) {
        case 'scale_to_fill': return 'cover';
        case 'stretch': return 'fill';
        case 'center': return 'none';
        case 'scale_to_fit': return 'contain';
        // scale_and_blur is a two-layer render (see below) — the
        // foreground uses contain, which this helper returns too so
        // unrecognized / null modes safely fall through to it.
        case 'scale_and_blur':
        default:
            return 'contain';
    }
}

// Resolve the slide duration (in ms) from the `secondsPerSlide` prop,
// falling back to 10s when the prop is missing/invalid. Clamped to the
// same 1..90s range the backend validates.
function resolveSlideDurationMs(secondsPerSlide) {
    const n = Number(secondsPerSlide);
    if (!Number.isFinite(n)) return DEFAULT_SECONDS_PER_SLIDE * 1000;
    const clamped = Math.max(MIN_SECONDS_PER_SLIDE, Math.min(MAX_SECONDS_PER_SLIDE, Math.round(n)));
    return clamped * 1000;
}

// Resolve the Pusher app key + cluster the same way the Slides editor does
// (see app/frontend/components/slides/editor_view/editor_view.jsx). The
// billboard layout sets `window.PUSHER_KEY` / `window.PUSHER_CLUSTER` via
// the layout template; the fallbacks let tests + admin-preview boot.
function resolvePusherConfig() {
    return {
        key: (typeof window !== 'undefined' && window.PUSHER_KEY) || '',
        cluster: (typeof window !== 'undefined' && window.PUSHER_CLUSTER) || 'us2',
    };
}

class PlaylistSlideshow extends Component {
    constructor(props) {
        super(props);
        this.state = {
            currentIndex: 0,
            // Slide opacity for the crossfade (#322). Starts at 1 (first
            // slide already visible), drops to 0 during the 500ms fade-out
            // before the swap, then rises back to 1 as the swapped-in
            // slide fades up.
            slideOpacity: 1,
            showControls: false,
            // Live stream override (#253 + #254). `active=true` swaps the
            // slideshow render for the LiveStreamOverlay (YouTube iframe).
            // `fadeOut=true` triggers the 1s exit transition before the
            // playlist resumes (handleOverlayFadeComplete clears state).
            liveOverride: {
                active: false,
                fadeOut: false,
                sourceId: null,
                videoId: null,
                channelId: null,
                provider: null,
            },
        };
        this._timer = null;
        this._fadeTimer = null;
        this._mouseTimer = null;
        this._ready = false;
        this._readyTimer = null;
        this._pusher = null;
        this._channel = null;
        this._channelName = null;
        this._unmounted = false;
        // LRU cache of decoded Image objects, keyed by URL. Insertion
        // order IS the recency order: a re-touched entry is deleted and
        // re-set so it moves to the back of the Map, and eviction always
        // takes the first key.
        this._imageCache = new Map();
    }

    componentDidMount() {
        document.addEventListener('keydown', this.handleKeyDown);
        document.addEventListener('fullscreenchange', this.handleFullscreenChange);
        this.enterFullscreen();
        this.startTimer();
        this._readyTimer = setTimeout(() => { this._ready = true; }, 1000);
        this.initLiveStreamSubscription();
        this.warmSlides();
    }

    componentDidUpdate(prevProps, prevState) {
        // Re-subscribe when the device id changes (route swap reusing the
        // same component instance). Without this, the player silently
        // stays bound to the previous channel and misses every event on
        // the new one.
        if (prevProps.billboardOutputId !== this.props.billboardOutputId) {
            this.teardownLiveStreamSubscription();
            this.initLiveStreamSubscription();
        }
        // Warm the next slide once the current one has settled (the
        // index changes at the fade trough, so by the time this fires
        // the current slide's own request is already in flight or done).
        if (prevState.currentIndex !== this.state.currentIndex ||
            prevProps.items !== this.props.items) {
            this.warmSlides();
        }
    }

    componentWillUnmount() {
        this._unmounted = true;
        this.stopTimer();
        document.removeEventListener('keydown', this.handleKeyDown);
        document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
        if (this._mouseTimer) clearTimeout(this._mouseTimer);
        if (this._readyTimer) clearTimeout(this._readyTimer);
        if (this._fadeTimer) clearTimeout(this._fadeTimer);
        this.teardownLiveStreamSubscription();
        // Drop the retained bitmaps so an unmounted player doesn't hold
        // tens of megabytes alive until GC gets around to the component.
        this._imageCache.clear();
    }

    // ------------------------------------------------------------------
    // Slide preloading + retention (S3 spend)
    // ------------------------------------------------------------------

    // Warms the current slide and exactly PRELOAD_AHEAD slides after it.
    // The current slide is included so that a loop's second pass re-touches
    // its cache entry (keeping it recent) rather than letting the entry it
    // is actively displaying age out.
    warmSlides() {
        const { items } = this.props;
        if (!items || items.length === 0) return;
        for (let offset = 0; offset <= PRELOAD_AHEAD; offset += 1) {
            const index = (this.state.currentIndex + offset) % items.length;
            this.retainImage(slideUrl(items[index]));
        }
    }

    // Puts `url` in the LRU, creating and kicking off the decode on a
    // miss. A hit is re-inserted so it moves to the most-recent end —
    // that is what makes a loop's second pass free rather than
    // dependent on the browser HTTP cache.
    retainImage(url) {
        if (!url) return null;

        const existing = this._imageCache.get(url);
        if (existing) {
            this._imageCache.delete(url);
            this._imageCache.set(url, existing);
            return existing;
        }

        // `new Image()` starts the fetch + decode without mounting
        // anything; holding the reference is what keeps the decoded
        // bitmap alive across the loop.
        const image = new Image();
        image.decoding = 'async';
        image.src = url;
        this._imageCache.set(url, image);

        while (this._imageCache.size > RETAINED_IMAGES) {
            const oldest = this._imageCache.keys().next().value;
            this._imageCache.delete(oldest);
        }
        return image;
    }

    // ------------------------------------------------------------------
    // Live stream override (#253)
    // ------------------------------------------------------------------

    initLiveStreamSubscription() {
        const { billboardOutputId } = this.props;
        // Optional prop: admin preview omits it, so the override path stays
        // dormant and a real production stream going live can't accidentally
        // hijack the in-app preview.
        if (billboardOutputId === undefined || billboardOutputId === null) return;

        const { key, cluster } = resolvePusherConfig();
        try {
            this._pusher = new Pusher(key, { cluster, encrypted: true });
        } catch (err) {
            // Pusher unavailable (e.g., key not configured in dev). Bail out
            // gracefully so the slideshow itself still works.
            this._pusher = null;
            return;
        }

        this._channelName = `billboard-output-${billboardOutputId}`;
        this._channel = this._pusher.subscribe(this._channelName);
        this._channel.bind('live_stream_source.went_live', this.handleWentLive);
        this._channel.bind('live_stream_source.ended', this.handleEnded);

        // Pusher reconnects automatically after a transient drop. After a
        // successful (re)connect, ask the server what the current live state
        // is so we don't sit on a stale override (or miss one that started
        // while we were offline). Fires on the very first connect too — that
        // initial call is harmless and surfaces any active stream the player
        // booted into mid-broadcast.
        if (this._pusher.connection && typeof this._pusher.connection.bind === 'function') {
            this._pusher.connection.bind('connected', this.handlePusherConnected);
        }
    }

    teardownLiveStreamSubscription() {
        if (this._channel && this._channelName) {
            try { this._channel.unbind('live_stream_source.went_live', this.handleWentLive); } catch (e) {}
            try { this._channel.unbind('live_stream_source.ended', this.handleEnded); } catch (e) {}
        }
        if (this._pusher) {
            try {
                if (this._channelName) this._pusher.unsubscribe(this._channelName);
            } catch (e) {}
            try { this._pusher.disconnect(); } catch (e) {}
        }
        this._channel = null;
        this._channelName = null;
        this._pusher = null;
    }

    handleWentLive = (payload) => {
        if (this._unmounted) return;
        const p = payload || {};
        this.stopTimer();
        this.setState({
            liveOverride: {
                active: true,
                fadeOut: false,
                sourceId: p.source_id != null ? p.source_id : null,
                videoId: p.video_id || null,
                channelId: p.channel_id || null,
                provider: p.provider || null,
            },
        });
    }

    handleEnded = (payload) => {
        if (this._unmounted) return;
        // Defensive: if we get an `ended` while not in override (shouldn't
        // happen with the worker as written, but Pusher can replay), don't
        // disrupt the current playlist position.
        if (!this.state.liveOverride.active) return;
        // Also no-op if we're already mid-fade (e.g. Pusher replay of ended
        // while the recovery fetch also flipped fadeOut).
        if (this.state.liveOverride.fadeOut) return;

        // Two-phase exit (#254). Phase 1: keep the overlay mounted (active=true)
        // but flip fadeOut so the iframe transitions opacity 1 → 0 over 1s.
        // Phase 2 fires from LiveStreamOverlay's onFadeComplete (see
        // handleOverlayFadeComplete), where we tear down the override and
        // restart the playlist from index 0 per the #229 spec.
        this.setState((prev) => ({
            liveOverride: {
                ...prev.liveOverride,
                fadeOut: true,
            },
        }));
    }

    handleOverlayFadeComplete = () => {
        if (this._unmounted) return;
        this.setState({
            currentIndex: 0,
            liveOverride: {
                active: false,
                fadeOut: false,
                sourceId: null,
                videoId: null,
                channelId: null,
                provider: null,
            },
        });
        // Locked spec for #229: "return to scheduled playlist from BEGINNING".
        this.stopTimer();
        this.startTimer();
    }

    handlePusherConnected = () => {
        const { billboardOutputId } = this.props;
        if (!billboardOutputId) return;
        if (typeof fetch !== 'function') return;
        const url = `/api/v2/devices/${billboardOutputId}/live_stream_state`;
        // credentials:'include' so any cookie-based session goes through; the
        // TV-display mount may also send a bearer token via auth headers,
        // which is a wiring concern for whoever instantiates the player.
        fetch(url, { credentials: 'include' })
            .then((res) => (res && res.ok ? res.json() : null))
            .then((data) => {
                if (!data || this._unmounted) return;
                if (data.live === true && !this.state.liveOverride.active) {
                    this.handleWentLive({
                        source_id: data.source_id,
                        billboard_output_id: billboardOutputId,
                        channel_id: data.channel_id,
                        video_id: data.video_id,
                        provider: data.provider,
                    });
                } else if (data.live === false && this.state.liveOverride.active) {
                    this.handleEnded({
                        source_id: data.source_id,
                        billboard_output_id: billboardOutputId,
                        channel_id: data.channel_id,
                    });
                }
            })
            .catch(() => { /* network blip — next reconnect will retry */ });
    }

    // ------------------------------------------------------------------

    enterFullscreen() {
        const el = document.documentElement;
        if (el.requestFullscreen) {
            el.requestFullscreen().catch(() => {});
        } else if (el.webkitRequestFullscreen) {
            el.webkitRequestFullscreen();
        }
    }

    exitFullscreen() {
        if (document.fullscreenElement) {
            document.exitFullscreen().catch(() => {});
        } else if (document.webkitFullscreenElement) {
            document.webkitExitFullscreen();
        }
    }

    handleFullscreenChange = () => {
        if (!document.fullscreenElement && !document.webkitFullscreenElement) {
            this.props.onClose();
        }
    }

    handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            this.exitFullscreen();
            this.props.onClose();
        } else if (e.key === 'ArrowRight') {
            this.nextSlide();
        } else if (e.key === 'ArrowLeft') {
            this.prevSlide();
        }
    }

    handleMouseMove = () => {
        if (!this._ready) return;
        this.setState({ showControls: true });
        if (this._mouseTimer) clearTimeout(this._mouseTimer);
        this._mouseTimer = setTimeout(() => {
            if (this._unmounted) return;
            this.setState({ showControls: false });
        }, 3000);
    }

    startTimer() {
        const durationMs = resolveSlideDurationMs(this.props.secondsPerSlide);
        this._timer = setInterval(() => {
            this.nextSlide();
        }, durationMs);
    }

    stopTimer() {
        if (this._timer) {
            clearInterval(this._timer);
            this._timer = null;
        }
        if (this._fadeTimer) {
            clearTimeout(this._fadeTimer);
            this._fadeTimer = null;
        }
    }

    // Crossfade advance (#322). Fade the current slide to black over
    // FADE_MS, swap the index at the trough, then let the new slide fade
    // back in over FADE_MS via the same opacity transition on the img
    // wrapper. `delta` is +1 for forward, -1 for backward. Reentry guard
    // (_fadeTimer set) protects against a keyboard mash landing on a
    // half-faded state.
    advanceBy(delta) {
        if (this._unmounted) return;
        if (this._fadeTimer) return;
        const { items } = this.props;
        if (!items || items.length <= 1) return;

        this.setState({ slideOpacity: 0 });
        this._fadeTimer = setTimeout(() => {
            if (this._unmounted) return;
            this._fadeTimer = null;
            this.setState(prev => ({
                currentIndex: (prev.currentIndex + delta + items.length) % items.length,
                slideOpacity: 1,
            }));
        }, FADE_MS);
    }

    nextSlide = () => {
        this.advanceBy(1);
    }

    prevSlide = () => {
        this.advanceBy(-1);
    }

    handleClose = () => {
        this.exitFullscreen();
        this.props.onClose();
    }

    renderLiveOverride() {
        const { liveOverride } = this.state;
        return (
            <LiveStreamOverlay
                videoId={liveOverride.videoId}
                sourceId={liveOverride.sourceId}
                channelId={liveOverride.channelId}
                provider={liveOverride.provider}
                fadeOut={liveOverride.fadeOut}
                onFadeComplete={this.handleOverlayFadeComplete}
            />
        );
    }

    render() {
        const { items } = this.props;
        const { currentIndex, showControls, slideOpacity, liveOverride } = this.state;

        if (liveOverride.active) {
            return this.renderLiveOverride();
        }

        if (!items || items.length === 0) return null;

        const currentItem = items[currentIndex];
        // Full resolution on purpose — this is the viewing surface, not a
        // preview grid. See slideUrl().
        const url = slideUrl(currentItem);
        const displayMode = currentItem.display_mode || 'scale_to_fit';

        return (
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: '#000',
                    zIndex: 99999,
                    cursor: showControls ? 'default' : 'none',
                }}
                onMouseMove={this.handleMouseMove}
            >
                {/* Slide frame — opacity transitions handle the 0.5s
                    crossfade (#322). Wrapped so the fade doesn't apply
                    to the overlay controls below. */}
                <div
                    data-testid="slide-frame"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        overflow: 'hidden',
                        opacity: slideOpacity,
                        transition: `opacity ${FADE_MS}ms linear`,
                    }}
                >
                    {displayMode === 'scale_and_blur' ? (
                        <React.Fragment>
                            <img
                                src={url}
                                alt=""
                                aria-hidden="true"
                                style={{
                                    position: 'absolute',
                                    inset: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    transform: 'scale(1.1)',
                                    filter: 'blur(20px)',
                                }}
                            />
                            <img
                                src={url}
                                alt=""
                                style={{
                                    position: 'relative',
                                    zIndex: 1,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                }}
                            />
                        </React.Fragment>
                    ) : (
                        <img
                            src={url}
                            alt=""
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: objectFitFor(displayMode),
                                objectPosition: displayMode === 'center' ? 'center' : undefined,
                            }}
                        />
                    )}
                </div>

                {/* Close button */}
                <button
                    onClick={this.handleClose}
                    style={{
                        position: 'absolute',
                        top: 20,
                        right: 20,
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        color: '#fff',
                        border: 'none',
                        fontSize: 24,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: showControls ? 1 : 0,
                        transition: 'opacity 0.3s',
                        pointerEvents: showControls ? 'auto' : 'none',
                    }}
                >
                    &times;
                </button>

                {/* Progress dots */}
                <div
                    style={{
                        position: 'absolute',
                        bottom: 24,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        gap: 8,
                        opacity: showControls ? 1 : 0,
                        transition: 'opacity 0.3s',
                    }}
                >
                    {items.map((_, i) => (
                        <div
                            key={i}
                            style={{
                                width: i === currentIndex ? 12 : 8,
                                height: i === currentIndex ? 12 : 8,
                                borderRadius: '50%',
                                backgroundColor: i === currentIndex ? '#fff' : 'rgba(255,255,255,0.4)',
                                transition: 'all 0.3s',
                            }}
                        />
                    ))}
                </div>

                {/* Slide counter */}
                <div
                    style={{
                        position: 'absolute',
                        bottom: 24,
                        right: 24,
                        color: 'rgba(255,255,255,0.6)',
                        fontSize: 14,
                        opacity: showControls ? 1 : 0,
                        transition: 'opacity 0.3s',
                    }}
                >
                    {currentIndex + 1} / {items.length}
                </div>
            </div>
        );
    }
}

export default PlaylistSlideshow;
